import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  Divider,
  FormInputText,
  InputLabel,
  Svg,
  SvgBubble,
  Tooltip,
} from '@hopskipdrive/react-components';
import { useFlags } from '@rideiq/contexts/LDContext';
import { useQuery } from '@tanstack/react-query';

import { OTHER, RESIDENCE } from '../../../../constants/locationTypes';
import { useLocationOptions } from '../../../../hooks/useLocationOptions';
import { SelectLocation } from '../../../../locations/features/MapPins/SelectLocation';
import { Button } from '../../../../shared/components/Button';
import { PageHeading } from '../../../../shared/components/PageHeading';
import { colorTokens } from '../../../../shared/components/variables';
import { MustBeMetProtocolForm } from '../../MustBeMetProtocolForm/MustBeMetProtocolForm';
import { MustBeMetProtocolFormV2 } from '../../MustBeMetProtocolForm/MustBeMetProtocolFormV2';
import { normalizePhoneNumber } from '../../MustBeMetProtocolForm/phoneNumber';
import { MustBeMetProtocol } from '../../MustBeMetProtocolForm/types';
import { useMustBeMetProtocol } from '../../MustBeMetProtocolForm/useMustBeMetProtocol';
import { mustBeMetProtocolValidationSchema } from '../../MustBeMetProtocolForm/validationSchema';
import { mustBeMetProtocolValidationSchemaV2 } from '../../MustBeMetProtocolForm/validationSchemaV2';
import { FindAndReplace } from '../FindAndReplace';
import { labelForLocationType, tooltipForLocationType } from '../helpers';
import { RiderLocation } from '../Location/Location.types';
import { PrimaryChangeModal } from '../PrimaryChangeModal';
import { newLocationSchema } from '../validationSchema';

import { FieldWithLabel, PrimaryContainer } from './RiderLocationForm.styles';
import { RiderLocationFormData, RiderLocationFormType } from './RiderLocationForm.types';

interface AddLocationFormProps {
  location?: RiderLocation;
  initialValues?: RiderLocationFormType;
  mustBeMetValues?: MustBeMetProtocol;
  onSubmit: (data, locationDetails) => void;
  onCancel: () => void;
  onValidationError?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  associatedLocations: RiderLocation[];
  disabledLocationIds?: string[];
  riderSlug: string;
  isEditView?: boolean;
  hideSavedLocationsInFindAndReplace?: boolean;
}

const normalizeSavedPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const isUsNumber = digits.length === 10 && !value.trim().startsWith('+');
  const isUsNumberWithCountryCode = digits.length === 11 && digits.startsWith('1');

  if (!/^[\d\s()+.-]+$/.test(value) || !(isUsNumber || isUsNumberWithCountryCode)) {
    return value;
  }

  return normalizePhoneNumber(value);
};

export const RiderLocationForm: FC<AddLocationFormProps> = ({
  location,
  initialValues,
  onSubmit,
  onCancel,
  onValidationError,
  onLoadingChange,
  associatedLocations,
  disabledLocationIds,
  riderSlug,
  isEditView,
  hideSavedLocationsInFindAndReplace = false,
}) => {
  const { getMustBeMetContacts, isOnMasterSchoolList } = useMustBeMetProtocol();
  const { rideiqMbmAuthorizedGroupsRedesignEnabled } = useFlags();
  const [locationDetails, setLocationDetails] = useState<RiderLocation | undefined>(location);
  const riderLocationDefaults = useMemo(() => {
    const protocol = initialValues?.mustBeMetProtocol;

    if (!rideiqMbmAuthorizedGroupsRedesignEnabled || !protocol?.authorizedContactGroups) {
      return initialValues;
    }

    return {
      ...initialValues,
      mustBeMetProtocol: {
        ...protocol,
        authorizedContactGroups: protocol.authorizedContactGroups.map((group) =>
          group.saved
            ? { ...group, escalationPhoneNumber: normalizeSavedPhoneNumber(group.escalationPhoneNumber || '') }
            : group
        ),
      },
    };
  }, [initialValues, rideiqMbmAuthorizedGroupsRedesignEnabled]);

  const { handleSubmit, register, control, setValue, formState, unregister, resetField, ...rest } =
    useForm<RiderLocationFormData>({
      defaultValues: useMemo(() => {
        return riderLocationDefaults;
      }, [riderLocationDefaults]),
      resolver: yupResolver(
        newLocationSchema().concat(
          rideiqMbmAuthorizedGroupsRedesignEnabled
            ? mustBeMetProtocolValidationSchemaV2()
            : mustBeMetProtocolValidationSchema()
        )
      ),
    });
  const { errors, isDirty } = formState;
  const [isProtocolLoading, setIsProtocolLoading] = useState(true);

  const primaryLocations = associatedLocations?.filter((associatedLocation) => associatedLocation.primary);

  const watchAddress = useWatch({
    control,
    name: 'locationId',
  });

  const watchMustBeMet = useWatch({
    control,
    name: 'mustBeMet',
    defaultValue: initialValues?.mustBeMet,
  });

  const createDefaultIndividualGroup = (fetchedContacts, defaultIndividualGroup) => {
    if (fetchedContacts.length > 0 && defaultIndividualGroup) {
      setValue('mustBeMetProtocol.authorizedContactGroups', [defaultIndividualGroup]);
    } else {
      setValue('mustBeMetProtocol.authorizedContactGroups', []);
    }
  };

  const handleFindAndReplaceToggle = (isActive: boolean) => {
    if (!isActive) {
      unregister(['replaceLocation.id', 'replaceLocation.effectiveAt']);
      // Marks the form dirty so unchecking (with no other field changed) still allows Save to submit.
      setValue('replaceLocation', undefined, { shouldDirty: true });
    }
  };

  const resetFormFieldsOnAddressChange = () => {
    resetField('primary');
    resetField('mustBeMet');
    resetField('mustBeMetProtocol');
    handleFindAndReplaceToggle(false);
  };

  const mustBeMetContactsQuery = useQuery({
    queryKey: [
      'mustBeMetContacts',
      locationDetails?.locationType,
      locationDetails?.id,
      locationDetails?.placeId,
      riderSlug,
    ],
    queryFn: () =>
      getMustBeMetContacts(
        locationDetails?.locationType || '',
        locationDetails?.id,
        locationDetails?.placeId,
        riderSlug
      ),
    enabled: !!watchMustBeMet,
  });
  const contacts = mustBeMetContactsQuery.data?.contacts ?? [];
  const isContactsLoaded = mustBeMetContactsQuery.isSuccess;
  const isMbmLoading =
    rideiqMbmAuthorizedGroupsRedesignEnabled && !!watchMustBeMet && (!isContactsLoaded || isProtocolLoading);

  useEffect(() => {
    onLoadingChange?.(isMbmLoading);
  }, [isMbmLoading, onLoadingChange]);

  useEffect(() => {
    // MustBeMetProtocolFormV2 owns its own default-group population; skip it here to avoid a race.
    if (mustBeMetContactsQuery.isSuccess && !isEditView && !rideiqMbmAuthorizedGroupsRedesignEnabled) {
      createDefaultIndividualGroup(
        mustBeMetContactsQuery.data.contacts,
        mustBeMetContactsQuery.data.defaultIndividualGroup
      );
    }
  }, [
    mustBeMetContactsQuery.data,
    mustBeMetContactsQuery.isSuccess,
    isEditView,
    rideiqMbmAuthorizedGroupsRedesignEnabled,
  ]);

  useEffect(() => {
    if (riderLocationDefaults?.mustBeMetProtocol?.authorizedContactGroups?.length) {
      setValue(
        'mustBeMetProtocol.authorizedContactGroups',
        riderLocationDefaults.mustBeMetProtocol.authorizedContactGroups
      );
      setValue('mustBeMetProtocol.locationProtocol', riderLocationDefaults.mustBeMetProtocol.locationProtocol);
    }
  }, [riderLocationDefaults]);

  return (
    <FormProvider {...{ handleSubmit, setValue, register, formState, control, unregister, resetField, ...rest }}>
      <form
        id="rider-associated-location"
        aria-label="form"
        onSubmit={(event) => {
          if (isMbmLoading) {
            event.preventDefault();

            return;
          }

          void handleSubmit((data: RiderLocationFormData) => {
            if (isDirty) {
              onSubmit({ ...data, ...data.mustBeMetProtocol }, locationDetails);
            } else {
              onCancel();
            }
          }, onValidationError)(event);
        }}
      >
        {!isEditView && (
          <Address
            data-testid="address-dropdown"
            control={control}
            disabledLocationIds={disabledLocationIds}
            handleSelected={(locationOption) => {
              locationOption ? setLocationDetails(locationOption) : setLocationDetails(undefined);
              resetFormFieldsOnAddressChange();
            }}
            errors={errors}
          />
        )}

        {watchAddress ? (
          <Box mb="1rem">
            <Divider mt="24px" mb="12px" />
            <PageHeading variant="h2" sx={{ margin: '16px 0px', color: `${colorTokens.primary.main} !important` }}>
              Instructions for this rider
            </PageHeading>
          </Box>
        ) : null}
        {locationDetails?.locationType && locationDetails?.locationType !== OTHER ? (
          <PrimaryCheckbox
            control={control}
            setValue={setValue}
            type={locationDetails?.locationType}
            locationDetails={locationDetails}
            primaryLocations={primaryLocations}
          />
        ) : null}
        <MustBeMetCheckbox control={control} isHidden={!watchAddress} />

        {watchMustBeMet && rideiqMbmAuthorizedGroupsRedesignEnabled && mustBeMetContactsQuery.isError && (
          <Box>
            <Alert severity="error" message="Unable to load authorized contacts. Please try again before saving." />
            <Button color="tertiary" onClick={() => mustBeMetContactsQuery.refetch()}>
              Try again
            </Button>
          </Box>
        )}
        {watchMustBeMet && rideiqMbmAuthorizedGroupsRedesignEnabled && !mustBeMetContactsQuery.isError && (
          <MustBeMetProtocolFormV2
            onLoadingChange={setIsProtocolLoading}
            contacts={contacts}
            contactsLoaded={isContactsLoaded}
            isOnMasterSchoolList={isOnMasterSchoolList}
            defaultIndividualGroup={mustBeMetContactsQuery.data?.defaultIndividualGroup ?? null}
            accountLocationId={locationDetails?.id?.toString()}
          />
        )}
        {watchMustBeMet && !rideiqMbmAuthorizedGroupsRedesignEnabled && (
          <MustBeMetProtocolForm contacts={contacts} accountLocationId={locationDetails?.id?.toString()} />
        )}
        {watchAddress && riderSlug && (
          <FindAndReplace
            associatedLocations={associatedLocations}
            locationIdToReplace={locationDetails?.id}
            onToggle={handleFindAndReplaceToggle}
            hideSavedLocations={hideSavedLocationsInFindAndReplace}
          />
        )}
      </form>
    </FormProvider>
  );
};

const Address = ({ control, disabledLocationIds, handleSelected, errors }) => {
  const { debounceHandleQuery, locationOptions, saveNewLocation } = useLocationOptions();

  return (
    <FieldWithLabel>
      <InputLabel fieldtype="required" htmlFor="address">
        Address
      </InputLabel>
      <Controller
        control={control}
        name="locationId"
        render={({ field: { onChange } }) => (
          <SelectLocation
            id="locationId"
            data-testid="select-location"
            hidden={false}
            options={locationOptions}
            saveLocation={saveNewLocation}
            source="Rider"
            disableOptions={disabledLocationIds}
            disabledOptionKey="id"
            clearOnSelect={false}
            clearOnCancel={true}
            setInputValue={(location) => {
              debounceHandleQuery('', { force: true });
              handleSelected(location);
              onChange(location?.id);

              return location?.label;
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <FormInputText
                {...params}
                variant="outlined"
                placeholder="Search"
                onChange={(e) => {
                  debounceHandleQuery(e.target.value);
                }}
                error={!!errors.locationId}
                helperText={errors.locationId && errors.locationId?.message}
                sx={{
                  width: '29.5rem',
                }}
              />
            )}
          />
        )}
      />
    </FieldWithLabel>
  );
};

const PrimaryCheckbox = ({ control, setValue, type, locationDetails, primaryLocations }) => {
  const [primaryUpdateModal, setPrimaryUpdateModal] = useState<boolean>(false);
  const isResidence = type === RESIDENCE;
  const primaryLocation = primaryLocations?.find((location) => location.locationType === type);

  return (
    <>
      <Controller
        control={control}
        name="primary"
        render={({ field: { value, onChange, onBlur } }) => {
          return (
            <PrimaryContainer>
              <label>
                <Checkbox
                  onBlur={onBlur}
                  checked={value}
                  onChange={(e) => {
                    onChange(e.target.checked);

                    if (e.target.checked && primaryLocation) {
                      setPrimaryUpdateModal(true);
                    }
                  }}
                />
                {`This is the rider’s primary ${labelForLocationType(isResidence)}`}
              </label>
              <Tooltip title={tooltipForLocationType(isResidence)}>
                <SvgBubble>
                  <Svg icon="question" />
                </SvgBubble>
              </Tooltip>
            </PrimaryContainer>
          );
        }}
      />
      <PrimaryChangeModal
        isOpen={primaryUpdateModal === true}
        currentLocation={primaryLocation?.name || primaryLocation?.address}
        nextLocation={locationDetails?.name || locationDetails?.address}
        onCancel={() => {
          setValue('primary', false);
          setPrimaryUpdateModal(false);
        }}
        onConfirm={() => setPrimaryUpdateModal(false)}
      />
    </>
  );
};

const MustBeMetCheckbox = ({ control, isHidden }) => {
  if (isHidden) {
    return null;
  }

  return (
    <Controller
      control={control}
      name="mustBeMet"
      render={({ field: { value, onChange, onBlur } }) => {
        return (
          <PrimaryContainer>
            <label>
              <Checkbox
                onBlur={onBlur}
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
              />
              {'Rider must be met at drop-off'}
            </label>
          </PrimaryContainer>
        );
      }}
    />
  );
};
